import {
  accountSearchColumns,
  boPhanSearchColumns,
  loaiTaiSanSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  vuViecSearchColumns
} from '@/constants';
import { CellFieldV2 as CellField, SearchFieldV2 as SearchField } from '@/components/custom/erp';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import { AccountModel, BoPhan, LoaiTaiSanCongCu, Phi, VuViec } from '@/types/schemas';
import type { FormFieldState } from '../../../hooks';
import { FormMode } from '@/types/form';

export const getTSCCTableColumns = (
  state: FormFieldState,
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void,
  formMode: FormMode = 'add'
) => [
  {
    field: 'ma_ts',
    headerName: 'Mã tài sản',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='ma_ts'
        type='text'
        value={params.row.ma_ts || null}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_ts', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ten_ts',
    headerName: 'Tên tài sản',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='ten_ts'
        type='text'
        value={params.row.ten_ts}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ten_ts', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ma_lts',
    headerName: 'Loại TS/CC',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<LoaiTaiSanCongCu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.LOAI_TAI_SAN_CONG_CU}/`}
        searchColumns={loaiTaiSanSearchColumns}
        columnDisplay='ma_lts'
        dialogTitle='Danh mục loại tài sản công cụ'
        value={params.row.ma_lts_data?.ma_lts || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_lts_data', row)}
        disabled={formMode === 'view'}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_lts',
      resultField: 'ma_lts_data',
      detailEndpoint: QUERY_KEYS.LOAI_TAI_SAN_CONG_CU
    }
  },
  {
    field: 'ngay_mua',
    headerName: 'Ngày tăng',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='ngay_mua'
        type='date'
        value={params.row.ngay_mua || state.ngay_ct}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ngay_mua', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ngay_kh0',
    headerName: 'Ngày tính khấu hao',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='ngay_kh0'
        type='date'
        value={params.row.ngay_kh0 || state.ngay_ct}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ngay_kh0', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'so_ky_kh',
    headerName: 'Số kỳ khấu hao',
    width: 130,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='so_ky_kh'
        type='number'
        value={params.row.so_ky_kh || 0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_ky_kh', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ngay_kh_kt',
    headerName: 'Ngày kết thúc KH',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='ngay_kh_kt'
        type='date'
        value={params.row.ngay_kh_kt || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ngay_kh_kt', newValue)}
        disabled={formMode === 'view'}
      />
    ),
    nonWritable: true
  },
  {
    field: 'nguyen_gia_nt',
    headerName: 'Nguyên giá VND',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='nguyen_gia_nt'
        type='number'
        placeholder='0'
        value={params.row.nguyen_gia_nt}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'nguyen_gia_nt', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'gt_da_kh_nt',
    headerName: 'Giá trị đã KH VND',
    width: 160,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='gt_da_kh_nt'
        type='number'
        placeholder='0'
        value={params.row.gt_da_kh_nt}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'gt_da_kh_nt', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'gt_cl_nt',
    headerName: 'Giá trị còn lại VND',
    width: 160,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='gt_cl_nt'
        type='number'
        placeholder='0'
        value={params.row.gt_cl_nt}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'gt_cl_nt', newValue)}
        disabled={formMode === 'view'}
      />
    ),
    nonWritable: true
  },
  {
    field: 'gt_kh_ky_nt',
    headerName: 'Giá trị khấu hao 1 kỳ VND',
    width: 180,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='gt_kh_ky_nt'
        type='number'
        placeholder='0'
        value={params.row.gt_kh_ky_nt}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'gt_kh_ky_nt', newValue)}
        disabled={formMode === 'view'}
      />
    ),
    nonWritable: true
  },
  {
    field: 'so_hieu_ts',
    headerName: 'Số hiệu công cụ',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='so_hieu_ts'
        type='text'
        value={params.row.so_hieu_ts || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_hieu_ts', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'tk_ts',
    headerName: 'Tk tài sản',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay={'code'}
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_ts_data?.code || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'tk_ts_data', row)}
        disabled={formMode === 'view'}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'code',
      resultField: 'tk_ts_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'tk_kh',
    headerName: 'Tk khấu hao',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay={'code'}
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_kh_data?.code || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'tk_kh_data', row)}
        disabled={formMode === 'view'}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'code',
      resultField: 'tk_kh_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'tk_cp',
    headerName: 'Tk chi phí',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay={'code'}
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_cp_data?.code || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'tk_cp_data', row)}
        disabled={formMode === 'view'}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'code',
      resultField: 'tk_cp_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'ma_bp',
    headerName: 'Mã bộ phận',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<BoPhan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
        searchColumns={boPhanSearchColumns}
        columnDisplay='ma_bp'
        dialogTitle='Danh mục bộ phận'
        value={params.row.ma_bp_data?.ma_bp || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
        disabled={formMode === 'view'}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_bp',
      resultField: 'ma_bp_data',
      detailEndpoint: QUERY_KEYS.BO_PHAN
    }
  },
  {
    field: 'ma_vv',
    headerName: 'Mã vụ việc',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<VuViec>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
        searchColumns={vuViecSearchColumns}
        columnDisplay={'ma_vu_viec'}
        dialogTitle='Danh mục vụ việc'
        value={params.row.ma_vv_data?.ma_vu_viec || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
        disabled={formMode === 'view'}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vu_viec',
      resultField: 'ma_vv_data',
      detailEndpoint: QUERY_KEYS.VU_VIEC
    }
  },
  {
    field: 'ma_phi',
    headerName: 'Mã phí',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<Phi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.PHI}/`}
        searchColumns={phiSearchColumns}
        columnDisplay='ma_phi'
        dialogTitle='Danh mục phí'
        value={params.row.ma_phi_data?.ma_phi || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
        disabled={formMode === 'view'}
        className='w-full'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_phi',
      resultField: 'ma_phi_data',
      detailEndpoint: QUERY_KEYS.PHI
    }
  },
  {
    field: 'dvt',
    headerName: 'Đvt',
    width: 100,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='dvt'
        type='text'
        value={params.row.dvt || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'dvt', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 100,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        commitOnBlur
        name='so_luong'
        type='number'
        value={params.row.so_luong || 0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_luong', newValue)}
        disabled={formMode === 'view'}
      />
    )
  }
];
